import { lazy } from 'react';
import { retry } from './utils/retry';
import withTracker from './tracking/with_tracker';

const PageNotFound = lazy(() => retry(() => import('./components/errors/not_found')));

const LoginPage = lazy(() => retry(() => import('./components/login/login_page')));
const MagicLinkCallbackPage = lazy(() => retry(() => import('./components/login/magic_link_callback_page')));
const SignUpPage = lazy(() => retry(() => import('./components/sign_up/sign_up_page')));
const PrivacyPolicy = lazy(() => retry(() => import('./components/legal/privacy')));
const ReturnPolicy = lazy(() => retry(() => import('./components/legal/return_policy')));
const DeliveryPolicy = lazy(() => retry(() => import('./components/legal/delivery_policy')));
const ContactUsPage = lazy(() => retry(() => import('./components/contact/contact_us_page')));
const LogOutDispatcher = lazy(() => retry(() => import('./components/logout/logout_dispatcher')));
const AboutUsPage = lazy(() => retry(() => import('./components/about_us/about_us_page')));
const HowItWorksPage = lazy(() => retry(() => import('./components/how_it_works/how_it_works_page')));
const NYE20232024Page = lazy(() => retry(() => import('./components/nye_2023_2024/nye_2023_2024')));
const SellerStoryPage = lazy(() => retry(() => import('./components/seller/seller_story_page')));
const SpecificLFClipPage = lazy(() => retry(() => import('./components/lfclips/specific_lfclip_page')));
const LiveStreamsPage = lazy(() => retry(() => import('./components//live_streams/live_streams_page')));
const AllSellersPage = lazy(() => retry(() => import('./components/seller/all_sellers_page')));
const CartPage = lazy(() => retry(() => import('./components/cart/cart')));
const FavoritesPage = lazy(() => retry(() => import('./components/favorites/favorites_page')));
const SuccessfulCheckoutPage = lazy(() => retry(() => import('./components/checkout/results/successful_checkout_page')));
const FailedCheckoutPage = lazy(() => retry(() => import('./components/checkout/results/failed_checkout_page')));
const CanceledCheckoutPage = lazy(() => retry(() => import('./components/checkout/results/canceled_checkout_page')));
const StripeVerificationPage = lazy(() => retry(() => import('./components/checkout/stripe_verification_page')));
const TabbyVerificationPage = lazy(() => retry(() => import('./components/checkout/tabby_verification_page')));
const PrecheckoutPage = lazy(() => retry(() => import('./components/checkout/precheckout')));

const MyOrdersPage = lazy(() => retry(() => import('./components/orders/my_orders_page')));
const MyTicketsPage = lazy(() => retry(() => import('./components/ticketing/tickets_page')));
const ExperienceCatalogPage = lazy(() => retry(() => import('./components/experiences/catalog_page')));
const ExperienceDetailPage = lazy(() => retry(() => import('./components/experiences/detail_page')));
const UserProfile = lazy(() => retry(() => import('./components/profile/user_profile')));
const ProfilePreviewPage = lazy(() => retry(() => import('./components/profile_preview/user_profile_preview')));
const SubscribePage = lazy(() => retry(() => import('./components/subscribe_page/subscribe_page')));
const ForgotPasswordPage = lazy(() => retry(() => import('./components/login/forgot/forgot_password_page')));
const EmailVerificationPage = lazy(() => retry(() => import('./components/email_verification/email_verification_page')));
const ChangeEmailForm = lazy(() => retry(() => import('./components/email_verification/change_email_form')));
const MyPhoneNumbersPage = lazy(() => retry(() => import('./components/phone_numbers/my_phone_numbers_page')));
const PasswordResetVerdictPage = lazy(() => retry(() => import('./components/login/forgot/password_reset_verdict_page')));
const PasswordResetter = lazy(() => retry(() => import('./components/login/forgot/password_resetter')));
const SearchResultPage = lazy(() => retry(() => import('./components/search/search_result_page')));

// These are some relatively heavy pages, so we lazy load them.
const FrontPage = lazy(() => retry(() => import('./components/front_page/front_page')));
const FemaleFrontPage = lazy(() => retry(() => import('./components/gender_home_pages/female_home_page')));
const DiscoverPage = lazy(() => retry(() => import('./components/new_discover_page/discover_page')));
const WomenDiscoverPage = lazy(() => retry(() => import('./components/new_discover_page/women_discover_page')));
const MenDiscoverPage = lazy(() => retry(() => import('./components/new_discover_page/men_discover_page')));
const KidsDiscoverPage = lazy(() => retry(() => import('./components/new_discover_page/kids_discover_page')));
const MaleFrontPage = lazy(() => retry(() => import('./components/gender_home_pages/male_home_page')));
const SingleItemPage = lazy(() => retry(() => import('./components/shopping/single_item/single_item')));
const SingleArticlePage = lazy(() => retry(() => import('./components/articles/article_page')));
const SingleSellerApplicationPage = lazy(() => retry(() => import('./components/seller_applications/seller_application_page')));
const CheckoutPage = lazy(() => retry(() => import('./components/checkout/checkout')));
const MenShoppingPage = lazy(() => retry(() => import('./components/shopping/shop_men')));
const WomenShoppingPage = lazy(() => retry(() => import('./components/shopping/shop_women')));
const KidsShoppingPage = lazy(() => retry(() => import('./components/shopping/shop_kids')));
const ShoppingFiltersPage = lazy(() => retry(() => import('./components/shopping/shopping_filters_page')));
const TermsOfService = lazy(() => retry(() => import('./components/legal/tos')));
const CookiesPolicy = lazy(() => retry(() => import('./components/legal/cookie_policy')));
const AppDownloadPage = lazy(() => retry(() => import('./components/app_download_page/app_download_page')));
const AppBetaEnrollmentPage = lazy(() => retry(() => import('./components/app_beta_enrollment/app_beta_enrollment_page')));
const ReturnPoliciesPage = lazy(() => retry(() => import('./components/legal/return_policies_page')));
const DeliveryPoliciesPage = lazy(() => retry(() => import('./components/legal/delivery_policies_page')));
const CaliforniaConsumerPrivacyActDisclosurePage = lazy(() => {
  return retry(() => import('./components/legal/california_consumer_privacy_act_disclosure_page'));
});
const CreatorsPage = lazy(() => retry(() => import('./components/creators/creators_page')));
/* Export tracked + lazy variants of the route components */
export const TrackedReturnPolicy = withTracker(ReturnPolicy);
export const TrackedFrontPage = withTracker(FrontPage);
export const TrackedFemaleFrontPage = withTracker(FemaleFrontPage);
export const TrackedDiscoverPage = withTracker(DiscoverPage);
export const TrackedWomenDiscoverPage = withTracker(WomenDiscoverPage);
export const TrackedMenDiscoverPage = withTracker(MenDiscoverPage);
export const TrackedKidsDiscoverPage = withTracker(KidsDiscoverPage);
export const TrackedMaleFrontPage = withTracker(MaleFrontPage);
export const TrackedAppDownloadPage = withTracker(AppDownloadPage);
export const TrackedAppBetaEnrollmentPage = withTracker(AppBetaEnrollmentPage);
export const TrackedSignUpPage = withTracker(SignUpPage);
export const TrackedLoginPage = withTracker(LoginPage);
export const TrackedMagicLinkCallbackPage = withTracker(MagicLinkCallbackPage);
export const TrackedForgotPasswordPage = withTracker(ForgotPasswordPage);
export const TrackedLogOutDispatcher = withTracker(LogOutDispatcher);
export const TrackedUserProfile = withTracker(UserProfile);
export const TrackedMyOrdersPage = withTracker(MyOrdersPage);
export const TrackedMyTicketsPage = withTracker(MyTicketsPage);
export const TrackedExperienceCatalogPage = withTracker(ExperienceCatalogPage);
export const TrackedExperienceDetailPage = withTracker(ExperienceDetailPage);
export const TrackedSingleItemPage = withTracker(SingleItemPage);
export const TrackedArticlePage = withTracker(SingleArticlePage);
export const TrackedSellerApplicationPage = withTracker(SingleSellerApplicationPage);
export const TrackedHowItWorksPage = withTracker(HowItWorksPage);
export const TrackedNYE20232024Page = withTracker(NYE20232024Page);
export const TrackedCartPage = withTracker(CartPage);
export const TrackedFavoritesPage = withTracker(FavoritesPage);
export const TrackedPrecheckoutPage = withTracker(PrecheckoutPage);
export const TrackedCheckoutPage = withTracker(CheckoutPage);
export const TrackedSuccessfulCheckoutPage = withTracker(SuccessfulCheckoutPage);
export const TrackedFailedCheckoutPage = withTracker(FailedCheckoutPage);
export const TrackedCanceledCheckoutPage = withTracker(CanceledCheckoutPage);
export const TrackedStripeVerificationPage = withTracker(StripeVerificationPage);
export const TrackedTabbyVerificationPage = withTracker(TabbyVerificationPage);
export const TrackedSellerStoryPage = withTracker(SellerStoryPage);
export const TrackedSpecificLFClipPage = withTracker(SpecificLFClipPage);
export const TrackedLiveStreamsPage = withTracker(LiveStreamsPage);
export const TrackedAllSellersPage = withTracker(AllSellersPage);
export const TrackedEmailVerificationPage = withTracker(EmailVerificationPage);
export const TrackedChangeEmailPage = withTracker(ChangeEmailForm);
export const TrackedMyPhoneNumbersPage = withTracker(MyPhoneNumbersPage);
export const TrackedPasswordResetVerdictPage = withTracker(PasswordResetVerdictPage);
export const TrackedPasswordResetter = withTracker(PasswordResetter);
export const TrackedMenShoppingPage = withTracker(MenShoppingPage);
export const TrackedWomenShoppingPage = withTracker(WomenShoppingPage);
export const TrackedKidsShoppingPage = withTracker(KidsShoppingPage);
export const TrackedShoppingFiltersPage = withTracker(ShoppingFiltersPage);
export const TrackedTermsOfService = withTracker(TermsOfService);
export const TrackedPrivacyPolicy = withTracker(PrivacyPolicy);
export const TrackedDeliveryPolicy = withTracker(DeliveryPolicy);
export const TrackedCookiesPolicy = withTracker(CookiesPolicy);
export const TrackedCaliforniaConsumerPrivacyActDisclosurePage = withTracker(
  CaliforniaConsumerPrivacyActDisclosurePage);
export const TrackedSubscribePage = withTracker(SubscribePage);
export const TrackedContactUsPage = withTracker(ContactUsPage);
export const TrackedAboutUsPage = withTracker(AboutUsPage);
export const TrackedPageNotFound = withTracker(PageNotFound);
export const TrackedSearchResultPage = withTracker(SearchResultPage);
export const TrackedReturnPoliciesPage = withTracker(ReturnPoliciesPage);
export const TrackedDeliveryPoliciesPage = withTracker(DeliveryPoliciesPage);
export const TrackerProfilePreviewPage = withTracker(ProfilePreviewPage);
export const TrackedCreatorsPage = withTracker(CreatorsPage);
